import React from 'react';
import { useAccess } from '../../hooks/useAccess';

/**
 * Shared "مسئول" (assignee) rendering for every assignable admin list.
 *
 * Backend contract: each list populates `assignedTo` as `{ _id, name, email }`
 * (or leaves it null). Lists are scoped server-side — a regular admin only ever
 * sees their own items plus unassigned ones — so in practice this cell reads
 * «خودم» or «بدون مسئول» for them, and shows real names to super admins.
 */

export interface AssigneeRef {
  _id?: string;
  id?: string;
  name?: string;
  email?: string;
}

/** Accepts a populated object, a bare id string, or null/undefined. */
export type AssigneeValue = AssigneeRef | string | null | undefined;

const idOf = (v: AssigneeValue): string | null => {
  if (!v) return null;
  if (typeof v === 'string') return v;
  return v._id || v.id || null;
};

const nameOf = (v: AssigneeValue): string | null => {
  if (!v || typeof v === 'string') return null;
  return v.name || v.email || null;
};

interface Props {
  value: AssigneeValue;
  /** Render nothing but the text (no chip) — for dense cells. */
  plain?: boolean;
}

const AssigneeCell: React.FC<Props> = ({ value, plain }) => {
  const { user } = useAccess();
  const assigneeId = idOf(value);

  if (!assigneeId) {
    return plain ? (
      <span style={{ opacity: 0.6 }}>بدون مسئول</span>
    ) : (
      <span className="tag tag-sm tag-neutral">بدون مسئول</span>
    );
  }

  const isMe = !!user.id && assigneeId === user.id;
  // A populated ref gives us a name; a bare id (list not populated) can still be
  // labelled when it's the current user, otherwise fall back to a short id.
  const label = isMe ? 'خودم' : nameOf(value) || `#${assigneeId.slice(-6)}`;

  if (plain) return <span>{label}</span>;
  return <span className={`tag tag-sm ${isMe ? 'tag-success' : 'tag-info'}`}>{label}</span>;
};

export default AssigneeCell;
